import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ClipboardList, Calendar, User, Mail, X, Clock, Eye, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { collection, doc, getDoc, onSnapshot, query, setDoc, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatSubmittedAt, getDefaultEvaluationPeriod, NO_TIMESTAMP_LABEL } from '../lib/utils';
import { getPersonIkrs, evalSubcollectionFor } from '../lib/personIkrs';
import {
    REMINDER_TYPE_BY_STAGE, REMINDER_LABELS,
    buildRemindersForStage, queueReminders,
    summariseReminderHistory, formatRelativeTime, findRecentlyReminded,
} from '../lib/reminders';
import { EvaluationResponsesModal, SendReminderModal } from '../features/evaluations';

const PERIOD_LABELS = {
    Q1: 'Q1 (Apr–Jun)', Q2: 'Q2 (Jul–Sep)', Q3: 'Q3 (Oct–Dec)', Q4: 'Q4 (Jan–Mar)', Annual: 'Annual (Full Year)',
};

/** Stage a row can be toggled to, and the label for the button that does it. */
const STAGE_TOGGLES = {
    'next-steps': { to: 'complete', label: 'Mark complete', Icon: ArrowRight, style: 'bg-emerald-600 hover:bg-emerald-700' },
    complete: { to: 'next-steps', label: 'Back to next steps', Icon: ArrowLeft, style: 'bg-violet-600 hover:bg-violet-700' },
};

export default function EvaluationsSummaryView({
    users, teams, db: dbProp, activeFY: activeFYProp, viewer, okrs, okrsFY2627,
}) {
    const dbInstance = dbProp || db;
    /** Financial year comes from the header toggle — this tab has no switch of its own. */
    const summaryFY = activeFYProp || 'FY2526';
    const [selectedPeriod, setSelectedPeriod] = useState(() => getDefaultEvaluationPeriod(summaryFY));
    const [evaluationStatuses, setEvaluationStatuses] = useState({});
    const [submittedAtByUser, setSubmittedAtByUser] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedStage, setSelectedStage] = useState(null);
    /** Reminder documents for this FY and period, for the "reminded 2h ago" labels. */
    const [reminderDocs, setReminderDocs] = useState([]);
    /** { stageId, people } while the confirm dialog is open. */
    const [reminderRequest, setReminderRequest] = useState(null);
    /** The employee whose responses are being read, if any. */
    const [responsesFor, setResponsesFor] = useState(null);
    /** uid currently being moved between stages, so its buttons can disable. */
    const [movingUserId, setMovingUserId] = useState(null);
    const [stageError, setStageError] = useState('');

    const evalSubcollection = evalSubcollectionFor(summaryFY);
    /** The tab is already admin-gated in App.js; re-checked here so the write buttons cannot leak. */
    const isAdmin = viewer?.role === 'Admin';

    // Keep the period on the quarter we're actually in for the selected FY.
    useEffect(() => {
        setSelectedPeriod(getDefaultEvaluationPeriod(summaryFY));
        setSelectedStage(null);
    }, [summaryFY]);

    const stages = [
        { id: 'pending', label: 'Pending Self-Review', color: 'bg-yellow-500', bgLight: 'bg-yellow-50', borderColor: 'border-yellow-200', textColor: 'text-yellow-800', icon: '⏳', description: 'Employees who need to complete their self-evaluations' },
        { id: 'manager-review', label: 'Manager Feedback', color: 'bg-blue-500', bgLight: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-800', icon: '📝', description: 'Awaiting manager review and feedback' },
        { id: 'next-steps', label: 'Next Steps', color: 'bg-purple-500', bgLight: 'bg-purple-50', borderColor: 'border-purple-200', textColor: 'text-purple-800', icon: '📋', description: 'Pending completion of development plans' },
        { id: 'complete', label: 'Completed', color: 'bg-green-500', bgLight: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-800', icon: '✅', description: 'Fully completed evaluations' }
    ];

    // Fetch evaluation statuses for all users
    useEffect(() => {
        let cancelled = false;

        const fetchStatuses = async () => {
            setLoading(true);
            const statuses = {};
            const submitted = {};

            for (const user of users) {
                const userId = user.uid || user.id;
                if (!userId) continue;

                try {
                    const statusRef = doc(dbInstance, `users/${userId}/${evalSubcollection}/${selectedPeriod}/status/current`);
                    const statusSnap = await getDoc(statusRef);
                    if (statusSnap.exists()) {
                        const data = statusSnap.data();
                        statuses[userId] = data.status || 'pending';
                        // Written when the employee submitted; absent on evaluations
                        // submitted before the field existed.
                        submitted[userId] = data.selfEvaluationSubmittedAt || null;
                    } else {
                        statuses[userId] = 'pending';
                    }
                } catch (error) {
                    console.error(`Error fetching status for user ${userId}:`, error);
                    statuses[userId] = 'pending';
                }
            }

            if (cancelled) return;
            setEvaluationStatuses(statuses);
            setSubmittedAtByUser(submitted);
            setLoading(false);
        };

        if (users.length > 0) {
            fetchStatuses();
        }

        return () => { cancelled = true; };
    }, [users, selectedPeriod, dbInstance, evalSubcollection]);

    /**
     * Reminders already queued for this FY and period. Two equality filters, so no
     * composite index is needed. Non-fatal on error — the history is a courtesy
     * label, not part of the dashboard.
     */
    useEffect(() => {
        if (!isAdmin) return undefined;
        const remindersQuery = query(
            collection(dbInstance, 'emailReminders'),
            where('fy', '==', summaryFY),
            where('period', '==', selectedPeriod)
        );
        const unsubscribe = onSnapshot(
            remindersQuery,
            (snapshot) => setReminderDocs(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
            (error) => console.error('Could not read the reminder queue:', error)
        );
        return () => unsubscribe();
    }, [dbInstance, summaryFY, selectedPeriod, isAdmin]);

    const reminderHistory = useMemo(() => summariseReminderHistory(reminderDocs), [reminderDocs]);

    // Group users by their evaluation status
    const groupedUsers = useMemo(() => {
        const groups = {
            'pending': [],
            'manager-review': [],
            'next-steps': [],
            'complete': []
        };

        users.forEach(user => {
            const userId = user.uid || user.id;
            const status = evaluationStatuses[userId] || 'pending';
            if (groups[status]) {
                // Find manager info
                const manager = users.find(u => (u.uid || u.id) === user.managerId);
                groups[status].push({
                    ...user,
                    managerName: manager?.name || user.managerEmail || 'Not assigned',
                    managerEmail: manager?.email || user.managerEmail || ''
                });
            }
        });

        return groups;
    }, [users, evaluationStatuses]);

    // Calculate totals
    const totalUsers = users.length;
    const completionRate = totalUsers > 0 ? ((groupedUsers.complete.length / totalUsers) * 100).toFixed(1) : 0;

    /**
     * Move one person between the Next Steps and Completed stages.
     *
     * Rules already let an admin write any evaluation document, and the notification
     * Cloud Function only fires on the edge into `manager-review`, so neither
     * direction of this sends an email. The status map is a one-shot read, so it is
     * patched locally rather than re-running one getDoc per employee.
     */
    const moveStage = useCallback(async (user, nextStatus) => {
        const userId = user.uid || user.id;
        if (!userId || !isAdmin) return;

        setMovingUserId(userId);
        setStageError('');
        try {
            const statusRef = doc(dbInstance, `users/${userId}/${evalSubcollection}/${selectedPeriod}/status/current`);
            await setDoc(statusRef, {
                userId,
                period: selectedPeriod,
                status: nextStatus,
                stageChangedBy: viewer?.email || null,
                stageChangedAt: serverTimestamp(),
                lastUpdated: new Date().toISOString(),
            }, { merge: true });
            setEvaluationStatuses(prev => ({ ...prev, [userId]: nextStatus }));
        } catch (error) {
            console.error('Could not change the evaluation stage:', error);
            setStageError(
                error.code === 'permission-denied'
                    ? 'Firestore rejected the change — your admin role could not be confirmed.'
                    : `Could not move ${user.name || 'this person'} (${error.code || error.message}).`
            );
        } finally {
            setMovingUserId(null);
        }
    }, [dbInstance, evalSubcollection, selectedPeriod, isAdmin, viewer]);

    /** IKRs for the person whose responses are open, so the modal can tab through them. */
    const responsesIkrs = useMemo(() => (
        responsesFor
            ? getPersonIkrs(responsesFor.uid || responsesFor.id, { okrs, okrsFY2627, fy: summaryFY })
            : []
    ), [responsesFor, okrs, okrsFY2627, summaryFY]);

    /** What the open confirm dialog is about: who gets mailed, who is skipped. */
    const reminderPreview = useMemo(() => {
        if (!reminderRequest) return null;
        const { stageId, people } = reminderRequest;
        const { docs, skipped } = buildRemindersForStage(stageId, people, users, {
            fy: summaryFY, period: selectedPeriod, requestedBy: viewer?.email,
        });
        const recentlyReminded = findRecentlyReminded(people, reminderHistory).map(person => ({
            name: person.name || person.email,
            lastQueuedAt: reminderHistory[person.uid || person.id]?.lastQueuedAt,
        }));
        return { type: REMINDER_TYPE_BY_STAGE[stageId], docs, skipped, recentlyReminded };
    }, [reminderRequest, users, summaryFY, selectedPeriod, viewer, reminderHistory]);

    const openReminderDialog = (stageId, people) => {
        setReminderRequest({ stageId, people });
    };

    const ReminderButton = ({ stageId, people, compact }) => {
        if (!isAdmin || !REMINDER_TYPE_BY_STAGE[stageId] || people.length === 0) return null;
        return (
            <button
                type="button"
                onClick={(event) => {
                    // The whole stage card is a click target that expands the table.
                    event.stopPropagation();
                    openReminderDialog(stageId, people);
                }}
                title={`${REMINDER_LABELS[REMINDER_TYPE_BY_STAGE[stageId]]} — ${people.length} ${people.length === 1 ? 'person' : 'people'}`}
                className={`inline-flex items-center gap-1.5 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors ${
                    compact ? 'px-2.5 py-1.5 text-[11px]' : 'px-3 py-2 text-xs'
                }`}
            >
                <Mail size={compact ? 12 : 14} />
                Send reminder{people.length > 1 ? ` (${people.length})` : ''}
            </button>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Evaluations Summary Dashboard</h2>
                        <p className="text-gray-600 mt-1">Track evaluation progress across all employees</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <ClipboardList className="text-indigo-600" size={28} />
                    </div>
                </div>

                {/* Period Selector — FY follows the header toggle */}
                <div className="flex items-center space-x-4 flex-wrap gap-y-2">
                    <label className="text-sm font-medium text-gray-700">
                        <Calendar size={16} className="inline mr-2" />
                        Select Period:
                    </label>
                    <select
                        value={selectedPeriod}
                        onChange={(e) => {
                            setSelectedPeriod(e.target.value);
                            setSelectedStage(null);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    >
                        {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Overall Progress */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-lg shadow-md text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-medium opacity-90">Overall Completion Rate</h3>
                        <p className="text-4xl font-bold mt-2">{completionRate}%</p>
                        <p className="text-sm opacity-75 mt-1">{groupedUsers.complete.length} of {totalUsers} employees completed</p>
                    </div>
                    <div className="text-right">
                        <div className="text-6xl opacity-20">📊</div>
                    </div>
                </div>
                <div className="mt-4 bg-white/20 rounded-full h-3">
                    <div
                        className="bg-white rounded-full h-3 transition-all duration-500"
                        style={{ width: `${completionRate}%` }}
                    ></div>
                </div>
            </div>

            {/* Summary Cards */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            ) : (
                <>
                    {stageError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                            {stageError}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {stages.map(stage => {
                            const people = groupedUsers[stage.id] || [];
                            const count = people.length;
                            const percentage = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(0) : 0;
                            const isSelected = selectedStage === stage.id;

                            return (
                                <div
                                    key={stage.id}
                                    onClick={() => setSelectedStage(isSelected ? null : stage.id)}
                                    className={`p-6 rounded-xl shadow-md cursor-pointer transition-all duration-200 border-2 flex flex-col ${
                                        isSelected
                                            ? `${stage.bgLight} ${stage.borderColor} ring-2 ring-offset-2 ring-${stage.color.replace('bg-', '')}`
                                            : 'bg-white border-transparent hover:shadow-lg hover:scale-[1.02]'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-3xl">{stage.icon}</span>
                                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${stage.color} text-white`}>
                                            {percentage}%
                                        </div>
                                    </div>
                                    <h3 className={`text-lg font-semibold ${stage.textColor}`}>{stage.label}</h3>
                                    <p className="text-3xl font-bold text-gray-900 mt-2">{count}</p>
                                    <p className="text-sm text-gray-500 mt-1">employees</p>
                                    <p className="text-xs text-gray-400 mt-2">{stage.description}</p>
                                    {REMINDER_TYPE_BY_STAGE[stage.id] && (
                                        <div className="mt-4 pt-3 border-t border-gray-100">
                                            <ReminderButton stageId={stage.id} people={people} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Detailed View */}
                    {selectedStage && (() => {
                        const stage = stages.find(s => s.id === selectedStage);
                        const people = groupedUsers[selectedStage] || [];
                        const toggle = isAdmin ? STAGE_TOGGLES[selectedStage] : null;
                        const canRemind = isAdmin && !!REMINDER_TYPE_BY_STAGE[selectedStage];

                        return (
                            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                                <div className={`px-6 py-4 ${stage?.bgLight} border-b ${stage?.borderColor}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center space-x-3">
                                            <span className="text-2xl">{stage?.icon}</span>
                                            <div>
                                                <h3 className={`text-lg font-semibold ${stage?.textColor}`}>
                                                    {stage?.label}
                                                </h3>
                                                <p className="text-sm text-gray-600">
                                                    {people.length} employees in this stage
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <ReminderButton stageId={selectedStage} people={people} />
                                            <button
                                                onClick={() => setSelectedStage(null)}
                                                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                                            >
                                                <X size={20} className="text-gray-500" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="overflow-x-auto">
                                    {people.length > 0 ? (
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self-eval submitted</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Needed</th>
                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {people.map((user, index) => {
                                                    const userId = user.uid || user.id;
                                                    const teamName = teams[user.teamId]?.name || user.teamId || 'Unknown';

                                                    let actionNeeded = '';
                                                    if (selectedStage === 'pending') {
                                                        actionNeeded = 'Employee needs to submit self-evaluation';
                                                    } else if (selectedStage === 'manager-review') {
                                                        actionNeeded = 'Manager needs to provide feedback';
                                                    } else if (selectedStage === 'next-steps') {
                                                        actionNeeded = 'Admin needs to complete next steps';
                                                    } else {
                                                        actionNeeded = 'No action needed';
                                                    }

                                                    const submittedAt = submittedAtByUser[userId];
                                                    const remindedAt = reminderHistory[userId]?.lastQueuedAt;
                                                    const isMoving = movingUserId === userId;

                                                    return (
                                                        <tr key={userId || index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                                                        <User size={20} className="text-indigo-600" />
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                                        <div className="text-sm text-gray-500 flex items-center">
                                                                            <Mail size={12} className="mr-1" />
                                                                            {user.email}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                                                                    {teamName}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">{user.managerName}</div>
                                                                {user.managerEmail && (
                                                                    <div className="text-xs text-gray-500">{user.managerEmail}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {user.role || 'Member'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                {selectedStage === 'pending' && !submittedAt ? (
                                                                    <span className="text-xs text-gray-400 italic">Not submitted</span>
                                                                ) : (
                                                                    <span className={`text-xs ${submittedAt ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                                                                        {submittedAt ? formatSubmittedAt(submittedAt) : NO_TIMESTAMP_LABEL}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center text-sm">
                                                                    <Clock size={14} className={`mr-2 ${
                                                                        selectedStage === 'complete' ? 'text-green-500' : 'text-orange-500'
                                                                    }`} />
                                                                    <span className={selectedStage === 'complete' ? 'text-green-600' : 'text-orange-600'}>
                                                                        {actionNeeded}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center justify-end gap-2 flex-wrap">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setResponsesFor(user)}
                                                                        title={`Read ${user.name || 'their'} responses`}
                                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                                                                    >
                                                                        <Eye size={12} />
                                                                        View responses
                                                                    </button>
                                                                    {canRemind && (
                                                                        <div className="flex flex-col items-end gap-0.5">
                                                                            <ReminderButton stageId={selectedStage} people={[user]} compact />
                                                                            {remindedAt && (
                                                                                <span className="text-[10px] text-gray-400">
                                                                                    Reminded {formatRelativeTime(remindedAt)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {toggle && (
                                                                        <button
                                                                            type="button"
                                                                            disabled={isMoving}
                                                                            onClick={() => moveStage(user, toggle.to)}
                                                                            title={`Move ${user.name || 'this person'} to ${toggle.to === 'complete' ? 'Completed' : 'Next Steps'}`}
                                                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toggle.style}`}
                                                                        >
                                                                            <toggle.Icon size={12} />
                                                                            {isMoving ? 'Moving…' : toggle.label}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="p-8 text-center text-gray-500">
                                            <p>No employees in this stage</p>
                                        </div>
                                    )}
                                </div>

                                {toggle && people.length > 0 && (
                                    <div className="px-6 py-3 bg-gray-50/70 border-t border-gray-100">
                                        <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                                            <ShieldCheck size={12} className="text-gray-400" />
                                            Admin only. Moving a person between Next Steps and Completed sends no email.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Quick Stats */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Insights</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span className="text-yellow-600 font-semibold">⚠️ Attention Needed</span>
                                </div>
                                <p className="text-2xl font-bold text-yellow-800">
                                    {groupedUsers.pending.length + groupedUsers['manager-review'].length}
                                </p>
                                <p className="text-sm text-yellow-600">employees awaiting action</p>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span className="text-blue-600 font-semibold">📊 In Progress</span>
                                </div>
                                <p className="text-2xl font-bold text-blue-800">
                                    {groupedUsers['manager-review'].length + groupedUsers['next-steps'].length}
                                </p>
                                <p className="text-sm text-blue-600">evaluations being processed</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span className="text-green-600 font-semibold">✅ Completed</span>
                                </div>
                                <p className="text-2xl font-bold text-green-800">
                                    {groupedUsers.complete.length}
                                </p>
                                <p className="text-sm text-green-600">evaluations finalized</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <SendReminderModal
                isOpen={!!reminderRequest}
                onClose={() => setReminderRequest(null)}
                type={reminderPreview?.type}
                recipientCount={reminderPreview?.docs.length || 0}
                skipped={reminderPreview?.skipped || []}
                recentlyReminded={reminderPreview?.recentlyReminded || []}
                periodLabel={`${summaryFY === 'FY2627' ? 'FY 26-27' : 'FY 25-26'} · ${PERIOD_LABELS[selectedPeriod] || selectedPeriod}`}
                onConfirm={() => queueReminders(reminderPreview?.docs || [])}
            />

            <EvaluationResponsesModal
                isOpen={!!responsesFor}
                onClose={() => setResponsesFor(null)}
                employee={responsesFor}
                fy={summaryFY}
                period={selectedPeriod}
                ikrs={responsesIkrs}
                db={dbInstance}
            />
        </div>
    );
};
